import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/hooks/use-auth';
import {
  useArchiveWorkGroup,
  useDeleteWorkGroup,
  useUnarchiveWorkGroup,
  useUpdateWorkGroup,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';
import type { WorkGroupSummary } from '@/lib/api';

interface WorkGroupSettingsDialogProps {
  workGroupId: string;
  workGroup: WorkGroupSummary;
  privateAccessReason?: string;
  canEdit: boolean;
  canDelete: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkGroupSettingsDialog({
  workGroupId,
  workGroup,
  privateAccessReason,
  canEdit,
  canDelete,
  open,
  onOpenChange,
}: WorkGroupSettingsDialogProps) {
  const { user } = useAuth();
  const [name, setName] = useState(workGroup.name?.trim() ?? '');
  const [retireConfirmOpen, setRetireConfirmOpen] = useState(false);

  const updateMutation = useUpdateWorkGroup();
  const archiveMutation = useArchiveWorkGroup();
  const unarchiveMutation = useUnarchiveWorkGroup();
  const deleteMutation = useDeleteWorkGroup();

  const isArchived = workGroup.isArchived;
  const canRename =
    canEdit &&
    !isArchived &&
    (workGroup.type !== 'private' || workGroup.ownerId === user?.id);

  useEffect(() => {
    if (open) {
      setName(workGroup.name?.trim() ?? '');
    }
  }, [open, workGroup.name]);

  const handleSaveName = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: 'Work group name is required', variant: 'destructive' });
      return;
    }
    if (trimmed === (workGroup.name?.trim() ?? '')) {
      toast({ title: 'No changes to save' });
      return;
    }
    updateMutation.mutate(
      { workGroupId, name: trimmed },
      {
        onSuccess: () => {
          toast({ title: 'Work group renamed' });
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  const handleArchive = () => {
    archiveMutation.mutate(
      { workGroupId, privateAccessReason },
      {
        onSuccess: () => {
          toast({ title: 'Work group archived' });
          onOpenChange(false);
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  const handleUnarchive = () => {
    unarchiveMutation.mutate(
      { workGroupId, privateAccessReason },
      {
        onSuccess: () => {
          toast({ title: 'Work group unarchived' });
          onOpenChange(false);
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  const handleRetire = () => {
    deleteMutation.mutate(
      { workGroupId, privateAccessReason },
      {
        onSuccess: () => {
          toast({ title: 'Work group retired' });
          setRetireConfirmOpen(false);
          onOpenChange(false);
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  const lifecyclePending =
    archiveMutation.isPending || unarchiveMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="wg-settings-dialog">
          <DialogHeader>
            <DialogTitle>Work group settings</DialogTitle>
            <DialogDescription>
              Rename this work group or change its archive status. Type: {workGroup.type}
              {isArchived ? ' · Archived' : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {canRename ? (
              <section className="space-y-2">
                <Label htmlFor="wg-settings-name">Name</Label>
                <Input
                  id="wg-settings-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="off"
                  disabled={updateMutation.isPending}
                />
                <DialogFooter className="px-0 sm:justify-start">
                  <Button
                    type="button"
                    size="sm"
                    disabled={!name.trim() || updateMutation.isPending}
                    onClick={handleSaveName}
                  >
                    Save name
                  </Button>
                </DialogFooter>
              </section>
            ) : null}

            {canEdit || (canDelete && !isArchived) ? (
              <section className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">Lifecycle</h3>
                <div className="flex flex-wrap gap-2">
                  {canEdit && !isArchived ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={lifecyclePending}
                      onClick={handleArchive}
                    >
                      Archive
                    </Button>
                  ) : null}
                  {canEdit && isArchived ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={lifecyclePending}
                      onClick={handleUnarchive}
                    >
                      Unarchive
                    </Button>
                  ) : null}
                  {canDelete && !isArchived ? (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={lifecyclePending}
                      onClick={() => setRetireConfirmOpen(true)}
                    >
                      Retire
                    </Button>
                  ) : null}
                </div>
              </section>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={retireConfirmOpen} onOpenChange={setRetireConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire this work group?</AlertDialogTitle>
            <AlertDialogDescription>
              This archives the work group (soft delete). You can manage trashed items from the trash
              view when available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                handleRetire();
              }}
            >
              Retire
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
